import { createContext, useContext, useMemo } from 'react'
import * as React from 'react'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'

interface DrawerAskAIChatStateValue {
  submitMessage: (message: string, options?: { messageType?: string }) => Promise<void>
  messages: Array<MessageItem>
  isStreaming: boolean
}

interface DrawerAskAIProviderProps extends DrawerAskAIChatStateValue {
  children: React.ReactNode
}

const DrawerAskAIContext = createContext<DrawerAskAIChatStateValue | undefined>(undefined)

/**
 * Provides shared chat state to all DrawerAskAIFooter instances in the tree.
 *
 * Eliminates prop drilling of `submitMessage`, `messages`, and `isStreaming`
 * through intermediate canvas/drawer components that don't use them.
 *
 * ⚠️ Performance: `messages` is a high-frequency value that updates on every
 * streamed token. This is acceptable because:
 * - Consumer count is low (at most 4-5 drawers, typically 1 visible).
 * - The same re-renders occurred when these values were passed via props.
 * - Each consumer's `useDrawerAskAI` hook derives state via `useMemo`.
 */
export function DrawerAskAIProvider({
  children,
  submitMessage,
  messages,
  isStreaming,
}: DrawerAskAIProviderProps) {
  const value = useMemo<DrawerAskAIChatStateValue>(() => ({
    submitMessage,
    messages,
    isStreaming,
  }), [submitMessage, messages, isStreaming])

  return (
    <DrawerAskAIContext.Provider value={value}>
      {children}
    </DrawerAskAIContext.Provider>
  )
}

/**
 * Access shared chat state for drawer Ask AI functionality.
 *
 * Must be used within a DrawerAskAIProvider — throws if the provider
 * is missing so the error surfaces immediately during development.
 */
export function useDrawerAskAIChatState(): DrawerAskAIChatStateValue {
  const context = useContext(DrawerAskAIContext)
  if (!context) {
    throw new Error('useDrawerAskAIChatState must be used within a DrawerAskAIProvider')
  }
  return context
}

const noopSubmit = () => Promise.resolve()

/**
 * Default noop value for use in tests or storybook where a full
 * provider tree is not needed.
 */
export const defaultDrawerAskAIChatState: DrawerAskAIChatStateValue = {
  submitMessage: noopSubmit,
  messages: [],
  isStreaming: false,
}
